import { aclFeatures } from '@/lib/acl/aclFeatures';
import {
    ANALYZE_COMMUNITY,
    ANALYZE_CONTENT,
    ANALYZE_ENGAGEMENT,
    ANALYZE_INDUSTRY_BENCHMARK,
    ANALYZE_MENTIONS,
    ANALYZE_OVERVIEW,
    ANALYZE_REACH_IMPRESSIONS,
    CAPTIONS_LISTS,
    CONTENT_ALBUM_ANALYTICS,
    CONTENT_ALBUM_POSTS,
    CONTENT_ALBUM_STATS,
    CONTENT_ALBUMS,
    CONTENT_ALBUMS_COMPARATIVE_TABLE,
    CONTENT_POSTS,
    // DASHBOARD
    DASHBOARD,
    DASHBOARD_CUSTOMIZATION,
    DASHBOARD_GENERATE_CUSTOM_REPORT,
    DATA_ACCESS,
    GROUPS,
    INSPIRATION,
    LISTENING_COMPETITOR,
    LISTENING_COMPETITORS,
    LISTENING_FEED,
    LISTENING_FEEDS,
    LISTENING_HASHTAG,
    LISTENING_HASHTAGS,
    MANAGE_COMMENTS_TRACKER,
    MANAGE_DM_TRACKER,
    MANAGE_IMPORTANT_TRACKER,
    MANAGE_MENTIONS_TRACKER,
    MANAGE_RESOLVED_TRACKER,
    MEDIA_LIBRARY,
    // REPORTS
    REPORTS,
    REPORTS_HASHTAGS,
    SCHEDULER,
    SCHEDULER_COLLABORATION_NOTIFICATIONS,
    SCHEDULER_COLLABORATION_SETTINGS,
    SCHEDULER_FEED_VIEW
} from '@/lib/routes/privateRoutes';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    THREADS,
    TIKTOK_BUSINESS,
    X,
    YOUTUBE
} from '@/lib/socialProfile';

/**
 * Add pages so that content is displayed when the user selects a group instead of the empty state
 */
export const groupHasAccess = [
    ANALYZE_OVERVIEW,
    SCHEDULER,
    INSPIRATION,
    CONTENT_ALBUMS,
    CONTENT_ALBUMS_COMPARATIVE_TABLE,
    CONTENT_ALBUM_POSTS,
    CONTENT_ALBUM_STATS,
    CONTENT_ALBUM_ANALYTICS,
    MANAGE_COMMENTS_TRACKER,
    // User will be redirected to the comments tab
    MANAGE_MENTIONS_TRACKER,
    MANAGE_IMPORTANT_TRACKER,
    MANAGE_RESOLVED_TRACKER
];

/**
 * Pages are specified for a network and corresponding feature flag.
 */
export const networkHasAccessWithFeatureFlag = {
    [ANALYZE_OVERVIEW]: {
        [X]: {
            featureFlag: 'x_analytics_active'
        }
    },
    [ANALYZE_ENGAGEMENT]: {
        [X]: {
            featureFlag: 'x_analytics_active'
        }
    },
    [ANALYZE_COMMUNITY]: {
        [X]: {
            featureFlag: 'x_analytics_active'
        }
    },
    [ANALYZE_CONTENT]: {
        [X]: {
            featureFlag: 'x_analytics_active'
        }
    },
    [ANALYZE_REACH_IMPRESSIONS]: {
        [X]: {
            featureFlag: 'x_analytics_active'
        }
    },
    [ANALYZE_MENTIONS]: {
        [X]: {
            featureFlag: 'x_analytics_active'
        }
    }
} as const;

/**
 * If the page is not specified in networkHasAccess, all networks will have access to the page.
 */
export const networkHasAccess = {
    // Analytics - X will show empty states
    [ANALYZE_OVERVIEW]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [ANALYZE_ENGAGEMENT]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [ANALYZE_COMMUNITY]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [ANALYZE_CONTENT]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [ANALYZE_REACH_IMPRESSIONS]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS],
    [ANALYZE_MENTIONS]: [INSTAGRAM],
    [ANALYZE_INDUSTRY_BENCHMARK]: [INSTAGRAM, FACEBOOK],

    [DASHBOARD]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],

    [REPORTS]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [REPORTS_HASHTAGS]: [INSTAGRAM],

    // Content/Inspiration - X will show empty states
    [CONTENT_POSTS]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [CONTENT_ALBUMS]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [CONTENT_ALBUMS_COMPARATIVE_TABLE]: [
        INSTAGRAM,
        FACEBOOK,
        LINKEDIN,
        TIKTOK_BUSINESS,
        PINTEREST,
        YOUTUBE
    ],
    [CONTENT_ALBUM_POSTS]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [CONTENT_ALBUM_ANALYTICS]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [CONTENT_ALBUM_STATS]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE],
    [INSPIRATION]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, YOUTUBE, X, THREADS],

    [MANAGE_COMMENTS_TRACKER]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS],
    [MANAGE_MENTIONS_TRACKER]: [INSTAGRAM, FACEBOOK, LINKEDIN, TIKTOK_BUSINESS],
    [MANAGE_DM_TRACKER]: [INSTAGRAM, FACEBOOK],

    [LISTENING_HASHTAG]: [INSTAGRAM],
    [LISTENING_HASHTAGS]: [INSTAGRAM],
    [LISTENING_FEED]: [INSTAGRAM, FACEBOOK],
    [LISTENING_FEEDS]: [INSTAGRAM, FACEBOOK],
    [LISTENING_COMPETITOR]: [INSTAGRAM, FACEBOOK],
    [LISTENING_COMPETITORS]: [INSTAGRAM, FACEBOOK],

    [SCHEDULER]: [INSTAGRAM, FACEBOOK, X, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, THREADS],
    [SCHEDULER_FEED_VIEW]: [INSTAGRAM],
    [MEDIA_LIBRARY]: [INSTAGRAM, FACEBOOK, X, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, THREADS],
    [CAPTIONS_LISTS]: [INSTAGRAM, FACEBOOK, X, LINKEDIN, TIKTOK_BUSINESS, PINTEREST, THREADS]
} as const;

export const userPermissionsHasAccess: Record<string, string[]> = {
    // Dashboard
    [DASHBOARD_CUSTOMIZATION]: [aclFeatures.report.white_label.read],
    [DASHBOARD_GENERATE_CUSTOM_REPORT]: [aclFeatures.report.white_label.read],

    // CONTENT
    [CONTENT_ALBUMS]: [aclFeatures.content.campaign.read],
    [CONTENT_ALBUMS_COMPARATIVE_TABLE]: [aclFeatures.content.campaign.read],
    [CONTENT_ALBUM_POSTS]: [aclFeatures.content.campaign.read],
    [CONTENT_ALBUM_STATS]: [aclFeatures.content.campaign.read],
    [CONTENT_ALBUM_ANALYTICS]: [aclFeatures.content.campaign.read],

    // REPORTS
    [REPORTS_HASHTAGS]: [aclFeatures.listening.hashtag.read],

    // LISTENING
    [LISTENING_HASHTAG]: [aclFeatures.listening.hashtag.read],
    [LISTENING_HASHTAGS]: [aclFeatures.listening.hashtag.read],
    [LISTENING_FEED]: [aclFeatures.listening.feed.read],
    [LISTENING_FEEDS]: [aclFeatures.listening.feed.read],

    // PUBLISHING
    [SCHEDULER_COLLABORATION_SETTINGS]: [aclFeatures.publishing.external_collaboration.read],
    [SCHEDULER_COLLABORATION_NOTIFICATIONS]: [aclFeatures.publishing.external_collaboration.read],

    // SOCIAL PROFILES
    [GROUPS]: [aclFeatures.group.base.read],

    // DIRECT MESSAGES
    [MANAGE_DM_TRACKER]: [aclFeatures.direct_messages.base.read],

    // ACCOUNT
    [DATA_ACCESS]: [aclFeatures.account.data_access.read]
};
